// apps/roleforge-app/src/components/layout/AuditSidebar.tsx
//
// Dark left sidebar for the audit experience. Contains the brand mark, the
// audit step indicator (vertical), action items (e.g. "Invite teammate"), and
// the workspace + avatar menu at the bottom.

import { type ReactNode, useEffect, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { StepIndicator } from '../chat/StepIndicator';
import { AuditWorkspaceMenu } from './AuditWorkspaceMenu';
import { listInvitations } from '../../lib/api-client';
import { useAuth } from '../../lib/auth';
import {
  describeInviteSlots,
  type StakeholderInviteSlots,
} from '../../lib/roleforge-invite-limits';

interface Props {
  currentStep: 1 | 2 | 3 | 4;
  auditId: string;
  workspaceId?: string;
  workspaceName?: string;
  extraActions?: ReactNode;
}

function RoleforgeBrandMark() {
  return (
    <div className="px-4 py-4">
      <img
        src="/roleforge-logo-horizontal.svg"
        alt="RoleForge — AI-first business operations"
        className="block w-full h-auto"
      />
    </div>
  );
}

export function AuditSidebar({ currentStep, auditId, workspaceId, workspaceName, extraActions }: Props) {
  const location = useLocation();
  const { jwt } = useAuth();
  const invitePath = `/audits/${auditId}/invitations`;
  const inviteActive = location.pathname === invitePath;
  // Issue #1324: the badge shows the ORG-WIDE slot truth reported by the
  // server. It used to count this audit's invitations, which is not the number
  // the server enforces — a second audit showed "2 left" while every invite
  // 402'd.
  const [slots, setSlots] = useState<StakeholderInviteSlots | null>(null);

  useEffect(() => {
    if (!jwt) return;
    let cancelled = false;

    void listInvitations(jwt, auditId)
      .then((res) => {
        if (!cancelled) setSlots(res.slots ?? null);
      })
      .catch(() => {
        if (!cancelled) setSlots(null);
      });

    return () => {
      cancelled = true;
    };
  }, [jwt, auditId]);

  return (
    <aside
      data-testid="audit-sidebar"
      className="dark-surface sticky top-0 z-30 flex h-screen w-64 shrink-0 flex-col border-r border-line bg-surface text-ink"
    >
      <div className="border-b border-line">
        <RoleforgeBrandMark />
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-5">
        <div className="mb-4">
          <h3 className="px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-graphite">
            Your audit
          </h3>
          <StepIndicator currentStep={currentStep} orientation="vertical" />
        </div>
      </nav>

      <div className="border-t border-line px-3 py-3">
        <Link
          to={invitePath}
          className={`flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-colors ${
            inviteActive
              ? 'bg-coral text-white shadow-[0_12px_30px_rgba(254,89,99,0.18)]'
              : 'text-graphite hover:bg-card hover:text-ink'
          }`}
          data-testid="sidebar-action-invite"
        >
          <UserPlus className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">Invite teammate</span>
          <span
            data-testid="sidebar-invite-slots"
            className="rounded-md bg-card px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-mint"
          >
            {describeInviteSlots(slots).remaining}
          </span>
        </Link>
        {extraActions}
      </div>

      <AuditWorkspaceMenu workspaceId={workspaceId} workspaceName={workspaceName} />
    </aside>
  );
}
